import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { API } from '../api';
import { TabTable } from '../ui/EntityPage';
import EmptyState from '../ui/EmptyState';
import { StatusBadge } from '../ui/Badge';
import { ticketPath } from '../ui/entityRoutes';
import { formatCount, formatDateTime, teamLabel } from '../ui/format';
import usePagedQuery from '../ui/usePagedQuery';
import { ticketModeLabel, ticketName, ticketTypeLabel } from './ticketFields';

const muted = (text) => <span className="text-graphite">{text}</span>;

const COLUMNS = [
  {
    key: 'ticket_name',
    header: 'Ticket',
    grow: true,
    minWidth: 240,
    fullText: ticketName,
    render: (t) => (
      <Link to={ticketPath(t.id)} className="font-mono text-id text-signal hover:underline">
        {ticketName(t)}
      </Link>
    ),
  },
  { key: 'ticket_type', header: 'Type', render: (t) => ticketTypeLabel(t.ticket_type) || muted('—') },
  { key: 'flow_type', header: 'Team', render: (t) => teamLabel(t.flow_type) || muted('Not set') },
  { key: 'status', header: 'Status', render: (t) => <StatusBadge status={t.status} /> },
  { key: 'mode', header: 'Mode', render: (t) => ticketModeLabel(t.mode) || muted('—') },
  { key: 'priority', header: 'Priority', render: (t) => t.priority || muted('Not set') },
  { key: 'hosts', header: 'Hosts', align: 'right', render: (t) => formatCount(t.host_count) },
  { key: 'created', header: 'Created', render: (t) => <span className="text-graphite tabular-nums">{formatDateTime(t.created_at)}</span> },
];

// Tickets that contain this entity. filter: { cve } | { hostname } | { package } (exact match).
export const fetchEntityTickets = (filter, { offset = 0, limit = 50 } = {}) => API.GET_TICKETS(null, { ...filter, offset, limit });

// version: change it to load the tickets again (after creating some).
export default function EntityTickets({ filter, emptyTitle, emptyText, emptyAction, version = 0 }) {
  const key = `${JSON.stringify(filter)}|${version}`;
  const fetchPage = useCallback((paging) => fetchEntityTickets(filter, paging), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const query = usePagedQuery(fetchPage, key);

  return (
    <TabTable
      label="Tickets"
      columns={COLUMNS}
      query={query}
      rowKey={(t) => t.id}
      errorTitle="Tickets could not be loaded"
      empty={(
        <EmptyState title={emptyTitle} action={emptyAction}>
          {emptyText}
        </EmptyState>
      )}
    />
  );
}
